import React, { useEffect, useState } from 'react';

// components
import Alert from './Alert';
import Button from './Button';
import Countdown from './Countdown';
import KittySelector from './KittySelector';
import Spinner from './Spinner';

// hooks
import clsx from 'clsx';
import dayjs from 'dayjs';
import { get } from 'lodash';
import toast from 'react-hot-toast';
import { useAdventures } from '../hooks/useAdventures';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import { getAssets } from '../utils/getAssets';
import { getCreatedAssetsNoMetadata } from '../utils/getCreatedAssets';
import CollabSelector from './CollabSelector';
import FullScreenLoader from './FullScreenLoader';

function AdventurePreview({ adventure, index }) {
  const { address } = useAlgo();
  const { myKitties } = useKittiesStore();
  const { startAdventure, claimReward, resetAdventure, reloadAdventures } = useAdventures();
  const [party, setParty] = useState([]);
  const endDate = adventure.endDate && new Date(adventure.endDate);
  const isOver = endDate ? endDate < new Date() : false;

  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { adventures } = useAdventures();
  const [localAdventure, setLocalAdventure] = useState(adventure);
  const [localMyKitties, setLocalMyKitties] = useState(myKitties);
  const [localMyCollabNfts, setLocalMyCollabNfts] = useState([]);
  const [localMyCollabNftsLoading, setLocalMyCollabNftsLoading] = useState(false);
  const entries = adventures.filter((it) => it.entry).map((it) => it.entry);
  const kittiesInEntries = entries.map((it) => it.party).flat();
  const kittyIdsInEntries = kittiesInEntries.map((it) => it.id);
  const collab = localAdventure.requirements?.collab;
  const isCollab = !!collab;
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const adventureIsOver = localAdventure?.entry?.endDate ? new Date(adventure?.entry?.endDate) < new Date() : false;
  const adventureIsCancelled = localAdventure?.entry?.cancelled;

  const onSelect = (index, kitty) => {
    // add to party at index
    const newParty = [...party];
    newParty[index] = kitty;
    setParty(newParty);
  };

  const onStartAdventure = async () => {
    setError(null);
    setSuccess(false);

    // Disable starting new adventures - system is being phased out
    setError('Starting new adventures is no longer available. Please check out Shitty Jobs instead! You can still claim rewards for ongoing adventures.');
    return;

    // Original code kept for reference (unreachable)
    if (!adventure.active || isOver) {
      setError('This adventure can no longer be started');
      return;
    }

    try {
      setLoading(true);
      await startAdventure(adventure, party);
      toast.success(`${adventure.name} started!`);
      await reloadAdventures();
      setSuccess(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onResetAdventure = async () => {
    setError(null);
    setSuccess(false);

    try {
      setResetLoading(true);
      await resetAdventure(adventure.id);
      await reloadAdventures();
    } catch (error) {
      setError(error.message);
    } finally {
      setSuccess(false);
      setResetLoading(false);
    }
  };

  const onClaimReward = async () => {
    setError(null);
    setSuccess(false);

    try {
      setLoading(true);
      const res = await claimReward(
        adventure.entry,
        collab?.reward?.id
          ? collab?.reward?.id
          : typeof adventure.rewardsGold === 'number'
          ? parseInt(process.env.NEXT_PUBLIC_GOLDEN_TREASURE_ID)
          : parseInt(process.env.NEXT_PUBLIC_TREASURE_ID)
      );
      const it = { ...localAdventure };
      it.entry.claimed = true;
      it.entry.rewardAmount = res.amount;
      toast.success(`Claimed ${res.amount} Treasure Box!`);
      await reloadAdventures();
      setSuccess(true);
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onOpenDescription = () => {
    setDescriptionOpen(true);
  };

  const onCloseDescription = () => {
    setDescriptionOpen(false);
  };

  useEffect(() => {
    if (!isCollab || !address) return;
    (async function () {
      try {
        setLocalMyCollabNftsLoading(true);
        const myAssets = await getAssets(address);
        const addresses = collab.addresses;

        // need to loop through addresses
        for (const a of addresses) {
          const collectionNfts = await getCreatedAssetsNoMetadata(a);
          const myAssetIds = myAssets.map((it) => it['asset-id']);
          const myCollectionNFts = collectionNfts.filter((it) => myAssetIds.includes(it.index));

          // add to localMyCollabNfts if asset doesn't exist there
          for (const nft of myCollectionNFts) {
            const exists = localMyCollabNfts.find((it) => it.index === nft.index);
            if (!exists) {
              setLocalMyCollabNfts((prev) => [...prev, nft]);
            }
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLocalMyCollabNftsLoading(false);
      }
    })();
  }, [collab, address]);

  useEffect(() => {
    // checkForEntries();
    setLocalAdventure(adventure);

    if (!adventure.active || isOver) {
      setError('This adventure can no longer be started.');
    }
  }, [party, adventure]);

  useEffect(() => {
    let theKitties = [];

    if (localAdventure?.requirements?.traits?.length) {
      const filteredKitties = [];

      for (const kitty of myKitties) {
        for (const attribute of kitty.attributes) {
          if (localAdventure?.requirements?.traits.includes(attribute.value)) {
            filteredKitties.push(kitty);
          }
        }
      }

      if (kittyIdsInEntries.length) {
        theKitties = filteredKitties.filter((it) => !kittyIdsInEntries.includes(it.id));
      } else {
        theKitties = filteredKitties;
      }
    } else {
      if (kittyIdsInEntries.length) {
        theKitties = myKitties.filter((it) => !kittyIdsInEntries.includes(it.id));
      } else {
        theKitties = myKitties;
      }
    }

    // filter by level
    theKitties = theKitties?.filter((it) => it.bio.level >= localAdventure.requirements.level);

    // filter by litter
    if (localAdventure?.requirements?.litter) {
      if (localAdventure.requirements.litter === 'First Litter') {
        theKitties = theKitties?.filter((it) => it?.gen === 1);
      } else if (localAdventure.requirements.litter === 'Second Litter') {
        theKitties = theKitties?.filter((it) => it?.gen === 2);
      } else if (localAdventure.requirements.litter === 'Third Litter') {
        theKitties = theKitties?.filter((it) => it?.gen === 3);
      } else if (localAdventure.requirements.litter === 'Fourth Litter') {
        theKitties = theKitties?.filter((it) => it?.gen === 4);
      } else if (localAdventure.requirements.litter === 'all') {
        if (party.filter((it) => it?.gen === 3).length) {
          theKitties = theKitties?.filter((it) => it?.gen !== 3);
        }
        if (party.filter((it) => it?.gen === 1).length) {
          theKitties = theKitties?.filter((it) => it?.gen !== 1);
        }
        if (party.filter((it) => it?.gen === 2).length) {
          theKitties = theKitties?.filter((it) => it?.gen !== 2);
        }
        if (party.filter((it) => it?.gen === 4).length) {
          theKitties = theKitties?.filter((it) => it?.gen !== 4);
        }
      }
    }

    setLocalMyKitties(theKitties);
  }, [JSON.stringify(localAdventure), myKitties?.length, JSON.stringify(party)]);

  return (
    <div style={{ zIndex: 99 - index }} className='relative flex flex-col bg-gray-700 rounded-lg'>
      <FullScreenLoader loading={loading} label='Loading...' />
      <img
        src={localAdventure.image}
        alt={`${localAdventure.name} artwork`}
        className='h-full w-full object-cover md:inset-0 z-10 absolute inset-0 rounded-lg'
      />

      <div className='relative flex flex-col rounded-lg bg-gray-900/80 p-4 z-20 w-full h-full'>
        <div className='relative z-20 w-full gap-5 rounded-lg grid grid-cols-1'>
          <div className='col-span-1'>
            <div className='flex flex-col'>
              <div className='flex gap-6'>
                <div className='w-full'>
                  <h2 className='mb-2 text-2xl font-bold flex justify-between gap-3'>
                    <span>{localAdventure.name}</span>
                    <div className='flex items-center gap-3'>
                      <button type='button' onClick={onOpenDescription} className='text-white'>
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' className='w-[20px] text-gray-400'>
                          <path
                            d='M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z'
                            fill='currentColor'
                          />
                        </svg>
                      </button>
                    </div>
                  </h2>
                  <div className='flex gap-4 md:gap-6 mb-1 items-center flex-wrap'>
                    <InfoWithIcon label={`${localAdventure.duration} day duration`} icon='calendar' />
                    {localAdventure.cooldown && <InfoWithIcon label={`${localAdventure.cooldown} day cooldown`} icon='stopwatch' />}
                    <InfoWithIcon label={<RequirementMessage adventure={localAdventure} />} icon='kitty' />
                    <InfoWithIcon label={`Level ${adventure.requirements.level}+`} icon='rankingStar' />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-span-1'>
            <div>
              <h2 className='mb-2 text-lg font-bold text-center'>Choose Party</h2>
              {isCollab ? (
                <div className={clsx(`gap-4 flex flex-col md:flex-row items-center justify-center mx-auto`)}>
                  <KittySelector label={1} kitty={get(localAdventure, `entry.party[0]`)} index={0} onSelect={onSelect} kitties={localMyKitties} />
                  <CollabSelector
                    nft={get(localAdventure, `entry.party[1]`)}
                    index={1}
                    collab={collab}
                    onSelect={onSelect}
                    myCollabs={localMyCollabNfts}
                    loading={localMyCollabNftsLoading}
                  />
                </div>
              ) : (
                <div className={clsx(`gap-4 flex flex-col md:flex-row items-center justify-center mx-auto flex-wrap`)}>
                  {new Array(localAdventure.requirements.partySize).fill(0).map((it, index) => (
                    <KittySelector
                      label={index + 1}
                      key={index}
                      kitty={get(localAdventure, `entry.party[${index}]`)}
                      index={index}
                      onSelect={onSelect}
                      kitties={localMyKitties}
                    />
                  ))}
                </div>
              )}

              {address && adventureIsCancelled && (
                <div className='flex flex-col items-center justify-center mt-8 space-y-8'>
                  <Alert type='danger'>
                    <p>One of your Shitty Kitties is no longer in your wallet. The adventure has been cancelled. Sorry! 😢</p>
                  </Alert>

                  <button type='button' className='font-bold text-primary-500' onClick={onResetAdventure}>
                    {resetLoading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Reset Adventure'}
                  </button>
                </div>
              )}

              {adventureIsOver && !localAdventure?.entry?.claimed && !adventureIsCancelled && (
                <>
                  <div className='flex justify-center mt-5 text-center'>
                    <p className='text-neutral-400'>You've finished the adventure! Claim your reward!</p>
                  </div>
                  <div className='relative flex flex-col items-center justify-center mt-5'>
                    <Button disabled={!localAdventure?.entry || localAdventure?.entry?.claimed} onClick={onClaimReward}>
                      {loading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Claim Reward'}
                    </Button>
                  </div>
                </>
              )}

              {!adventureIsOver && localAdventure.entry && !adventureIsCancelled && (
                <div className='flex justify-center mt-5 text-center flex-col'>
                  <p className='text-neutral-400'>
                    Ends in <Countdown date={localAdventure?.entry?.endDate} simple />
                  </p>
                  <p className='text-neutral-400 text-sm mt-1'>{dayjs(localAdventure?.entry?.endDate).format('MMMM D, YYYY h:mm A')}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            {!adventureIsOver && !adventureIsCancelled && (
              <div className='flex flex-col items-center justify-center gap-6 mt-4'>
                {error && (
                  <Alert type='danger'>
                    <p>{error}</p>
                  </Alert>
                )}

                {address && (
                  <>
                    <div>
                      <Button
                        disabled={
                          localAdventure.entry ||
                          (localMyKitties.length === 0 && party.length !== localAdventure.requirements.partySize) ||
                          !adventure.active ||
                          isOver
                        }
                        onClick={onStartAdventure}
                      >
                        {loading ? (
                          <Spinner dimensionClasses='w-6 h-6' />
                        ) : localAdventure.entry ? (
                          'Adventure in progress'
                        ) : success ? (
                          'Successfully started adventure! 💪'
                        ) : (
                          'Start Adventure'
                        )}
                      </Button>
                    </div>

                    {localAdventure.entry && (
                      <div className='flex justify-center mt-3'>
                        <button type='button' className='font-bold text-primary-500' onClick={onResetAdventure}>
                          {resetLoading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Reset Adventure'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {localAdventure?.entry?.claimed && (
              <>
                <div className='flex flex-col items-center justify-center mt-5'>
                  <Alert>
                    <p>Successfully claimed {localAdventure?.entry?.rewardAmount} Shitty Treasure Box! 📦</p>
                  </Alert>
                </div>
                <div className='flex justify-center mt-6'>
                  <button type='button' className='font-bold text-primary-500' onClick={onResetAdventure}>
                    {resetLoading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Reset Adventure'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {descriptionOpen && (
        <div className='absolute inset-0 w-full h-full z-30 bg-gray-900/95 flex items-center justify-center rounded-lg p-6 max-h-full overflow-y-scroll'>
          <Button
            onClick={onCloseDescription}
            theme='none'
            className='absolute top-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black text-white'
          >
            <span className='sr-only'>Close modal</span>
            <svg width={16} height={16} viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M8.0009 6.23242L14.1884 0.0449219L15.9559 1.81242L9.7684 7.99992L15.9559 14.1874L14.1884 15.9549L8.0009 9.76742L1.8134 15.9549L0.0458984 14.1874L6.2334 7.99992L0.0458984 1.81242L1.8134 0.0449219L8.0009 6.23242Z'
                fill={'currentColor'}
              />
            </svg>
          </Button>
          <div>
            {adventure.endDate && (
              <div className='flex items-center gap-4 mb-4'>
                {adventure.endDate && (
                  <div className={clsx('text-white px-3 py-1 text-sm font-bold rounded-full', isOver ? 'bg-red-500' : 'bg-gray-800')}>
                    {!isOver ? (
                      <div className='flex items-center'>
                        <p>
                          Expires in <Countdown date={adventure.endDate} simple />
                        </p>
                      </div>
                    ) : (
                      <div className='flex items-center'>
                        <p>This adventure can no longer be started.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className='md:pr-12 text-lg leading-8 text-neutral-400'>{localAdventure.description}</p>
            <div className='flex flex-col justify-center gap-4 mt-4'>
              <Reward adventure={localAdventure} party={party} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoWithIcon({ label, icon }) {
  const icons = {
    calendar: (
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' className='w-[18px] flex-shrink-0'>
        <path
          d='M152 64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0C141.3 0 152 10.75 152 24V64zM48 448C48 456.8 55.16 464 64 464H384C392.8 464 400 456.8 400 448V192H48V448z'
          fill='currentColor'
        />
      </svg>
    ),
    stopwatch: (
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' className='w-[18px] flex-shrink-0'>
        <path
          d='M248 320C248 333.3 237.3 344 224 344C210.7 344 200 333.3 200 320V216C200 202.7 210.7 192 224 192C237.3 192 248 202.7 248 216V320zM144 24C144 10.75 154.7 0 168 0H280C293.3 0 304 10.75 304 24C304 37.25 293.3 48 280 48H248V97.37C291.4 102.4 330.8 120.7 361.8 148.2L391 119C400.4 109.7 415.6 109.7 424.1 119C434.3 128.4 434.3 143.6 424.1 152.1L393.9 184C417.9 217.9 432 259.3 432 304C432 418.9 338.9 512 224 512C109.1 512 16 418.9 16 304C16 197.2 96.43 109.3 200 97.37V48H168C154.7 48 144 37.25 144 24V24zM224 464C312.4 464 384 392.4 384 304C384 215.6 312.4 144 224 144C135.6 144 64 215.6 64 304C64 392.4 135.6 464 224 464z'
          fill='currentColor'
        />
      </svg>
    ),
    kitty: (
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512' className='w-[20px] flex-shrink-0'>
        <path
          d='M361 0c10.9 0 21.3 4.3 29 12l36 36h44.1l36-36c7.7-7.7 18.1-12 29-12c22.6 0 41 18.3 41 41V160c0 70.7-57.3 128-128 128s-128-57.3-128-128V41c0-22.6 18.3-41 41-41zm7 160c0 44.2 35.8 80 80 80s80-35.8 80-80V57.9L501.7 84.3C494.2 91.8 484 96 473.4 96H422.6c-10.6 0-20.8-4.2-28.3-11.7L368 57.9V160zm48-32a16 16 0 1 1 0 32 16 16 0 1 1 0-32zm48 16a16 16 0 1 1 32 0 16 16 0 1 1 -32 0zM285 182l4.4-.4c2.2 16.6 7 32.4 13.9 46.9l-14 1.3C188.9 238.9 112 323.1 112 423.9v.1c0 22.1 17.9 40 40 40H256V400c0-17.7-14.3-32-32-32h-8c-13.3 0-24-10.7-24-24s10.7-24 24-24h8c39.4 0 72.2 28.5 78.8 66l93-54.7c7.4-4.4 16.6-4.4 24.1-.1s12.1 12.2 12.1 20.8v96c0 8.8 7.2 16 16 16s16-7.2 16-16V319.2c16.9-1.7 33.1-6 48-12.5V448c0 35.3-28.7 64-64 64s-64-28.7-64-64V394L304 441v23h24c13.3 0 24 10.7 24 24s-10.7 24-24 24H152c-48.6 0-88-39.4-88-88v-.1V184.5c0-20.2-15-37.2-35-39.7l-7.9-1C7.9 142.2-1.5 130.2 .2 117S13.8 94.5 27 96.2l7.9 1c44 5.5 77.1 42.9 77.1 87.3V279c40-53.7 101.7-90.5 173-97z'
          fill='currentColor'
        />
      </svg>
    ),
    rankingStar: (
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512' className='w-[20px] flex-shrink-0'>
        <path
          d='M353.8 54.1l52.3 7.5c9.3 1.4 13.2 12.9 6.4 19.8l-38 36.6 9 52.1c1.4 9.3-8.2 16.5-16.8 12.2l-46.6-24.4-46.9 24.8c-8.6 4.3-18.3-2.9-16.8-12.2l9-52.1-38-37c-6.8-6.8-2.9-18.3 6.4-19.8l52.3-7.5L309.8 6.3c4.3-8.6 16.5-8.3 20.4 0l23.6 47.8zM272 304V464h96V304H272zm-48 0c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48V464c0 26.5-21.5 48-48 48H272c-26.5 0-48-21.5-48-48V304zM48 368v96h96V368H48zM0 368c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V368zm592 32H496v64h96V400zm-96-48h96c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H496c-26.5 0-48-21.5-48-48V400c0-26.5 21.5-48 48-48z'
          fill='currentColor'
        />
      </svg>
    ),
  };

  return (
    <div className=' text-neutral-300 flex items-center gap-2'>
      {icons[icon] || icons.calendar}
      <span className='whitespace-nowrap'>{label}</span>
    </div>
  );
}

function Reward({ adventure, party }) {
  const collab = adventure.requirements?.collab;
  const isCollab = !!collab;

  const partyHasBonusTrait =
    adventure.bonusRewardTrait &&
    [...party, ...(adventure?.entry?.party || [])].some((member) => {
      if (!member?.attributes) return false;
      const attrs = member?.attributes.map((attr) => attr.value);
      const valid = adventure.bonusRewardTrait.some((trait) => attrs?.includes(trait));
      return valid;
    });

  const rewardName = isCollab
    ? collab.reward.name
    : typeof adventure.rewards === 'number'
    ? 'Shitty Treasure Box'
    : typeof adventure.rewardsGold === 'number'
    ? 'Golden Treasure Box'
    : 'Shitty Treasure Box';
  const imageUrl = isCollab
    ? collab.reward.image
    : typeof adventure.rewards === 'number'
    ? '/boxes/shitty-treasure.png'
    : typeof adventure.rewardsGold === 'number'
    ? '/boxes/golden-treasure.png'
    : '/boxes/shitty-treasure.png';
  const assetId = isCollab
    ? collab.reward.id
    : typeof adventure.rewards === 'number'
    ? process.env.NEXT_PUBLIC_TREASURE_ID
    : typeof adventure.rewardsGold === 'number'
    ? process.env.NEXT_PUBLIC_GOLDEN_TREASURE_ID
    : process.env.NEXT_PUBLIC_TREASURE_ID;

  return (
    <div className='max-w-lg'>
      <div className='flex items-center gap-6'>
        <div>
          <a href={`https://explorer.perawallet.app/asset/${assetId}`} target='_blank'>
            <img src={imageUrl} alt={rewardName} className='w-[100px] rounded-md' />
          </a>
        </div>

        <div className='space-y-5'>
          <div className='flex flex-col'>
            {typeof adventure.rewards === 'number' && (
              <p className=''>
                Rewards {partyHasBonusTrait ? adventure.rewards * 2 : adventure.rewards} {rewardName}
              </p>
            )}
            {typeof adventure.rewardsGold === 'number' && (
              <p className=''>
                Rewards {partyHasBonusTrait ? adventure.rewards * 2 : adventure.rewards} {rewardName}
              </p>
            )}
            {adventure.bonusRewardTrait && (
              <p className='text-sm text-gray-400'>
                Bonus Treasure Box for{' '}
                <strong>
                  {adventure.bonusRewardTrait
                    .map((trait) => {
                      if (trait === 'Paintbrush') {
                        trait = 'Paint Brush';
                      }
                      return trait;
                    })
                    .join('/')}
                </strong>{' '}
                traits
              </p>
            )}
            {/* <p className='text-lg text-neutral-400'>{rewardName}</p> */}
            {partyHasBonusTrait && <p className='text-primary-500 font-bold'>BONUS TREASURE</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function RequirementMessage({ adventure }) {
  const collab = adventure.requirements?.collab;
  const isCollab = !!collab;
  const needsBlock = collab?.blocks?.length > 0;

  if (isCollab) {
    return (
      <p>
        <strong>1 kitty</strong> and <strong>1 {collab.name}</strong>
        {needsBlock && (
          <>
            {' '}
            with <strong>{collab.blocks.join('and ')}</strong>
          </>
        )}
      </p>
    );
  }

  return (
    <p>
      {adventure.requirements.partySize}
      {adventure.requirements.litter && ` ${adventure.requirements.litter}`} {adventure.requirements.partySize === 1 ? ' kitty' : 'kitties'}
      {adventure?.requirements?.traits?.length > 0 && (
        <>
          {' '}
          with the <strong className='text-wrap'>{adventure?.requirements?.traits?.join(', ')}</strong>{' '}
          {adventure?.requirements?.traits?.length === 1 ? 'trait' : 'traits'}
        </>
      )}
    </p>
  );
}

export default AdventurePreview;
